/**
 * Crescent AI Packaging Assistant — chat widget. Floating, right-aligned,
 * exclusive to /crescent/* routes. Posts to /api/crescent/assistant/chat and
 * renders the assistant's grounded reply. If `ask_rfq` is true the user is
 * nudged to the RFQ page; if `escalate` is true a human-handoff banner shows.
 */
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, ArrowRight, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content: "Hello! Tell me what product you need to pack and your approximate quantity — I can help you identify the right Crescent packaging options and prepare the information our team needs for a quote.",
};

export default function CrescentAssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [escalate, setEscalate] = useState(false);
  const [askRfq, setAskRfq] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-crescent-chat", onOpen);
    return () => window.removeEventListener("open-crescent-chat", onOpen);
  }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/crescent/assistant/chat`, {
        message: text,
        history: nextMessages,
        session_id: sessionId,
      });
      setSessionId(data.session_id || sessionId);
      setMessages([...nextMessages, { role: "assistant", content: data.reply, sources: data.sources }]);
      setEscalate(!!data.escalate);
      setAskRfq(!!data.ask_rfq);
    } catch (e) {
      setMessages([...nextMessages, { role: "assistant", content: "I'm having trouble reaching our systems. Please try again, or request a quote and our team will follow up." }]);
    } finally { setSending(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="crescent-chat-fab"
        aria-label="Open AI Packaging Assistant"
        className={`fixed bottom-5 right-5 z-40 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-xl flex items-center gap-2 px-5 py-3.5 font-bold text-sm transition-all ${open ? "opacity-0 pointer-events-none" : ""}`}
      >
        <MessageCircle size={18} /> Ask Packaging AI
      </button>

      {open && (
        <div
          data-testid="crescent-chat-panel"
          className="fixed bottom-5 right-5 z-50 w-[380px] max-w-[calc(100vw-2.5rem)] h-[560px] max-h-[calc(100vh-2.5rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          <header className="flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} />
              <div>
                <div className="font-bold text-sm leading-tight">Crescent Packaging AI</div>
                <div className="text-[10px] opacity-80">Grounded on verified Crescent info only</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="crescent-chat-close" className="text-white/80 hover:text-white"><X size={18} /></button>
          </header>

          <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
                <div className={`max-w-[86%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-emerald-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                  {m.content}
                  {m.sources?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {m.sources.map((s, j) => (
                        <span key={j} className="text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full px-2 py-0.5">
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 inline-flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}
            {escalate && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900" data-testid="chat-escalate">
                Our Crescent sales team should confirm this. <Link to="/crescent/request-quote" className="font-bold underline">Request a quote →</Link>
              </div>
            )}
            {askRfq && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900" data-testid="chat-ask-rfq">
                Ready to submit this as a formal RFQ? <Link to="/crescent/request-quote" className="font-bold underline inline-flex items-center gap-0.5">Continue to RFQ <ArrowRight size={12} /></Link>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form className="p-3 border-t border-slate-200 bg-white flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="What are you packing?"
              data-testid="crescent-chat-input"
              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 outline-none"
            />
            <button
              type="submit"
              data-testid="crescent-chat-send"
              disabled={sending || !input.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg w-10 h-10 flex items-center justify-center"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
